<template>
  <div class="kl-matrix kl-text px-1">
    <NavigationBreadcrumb
      :items="breadcrumb_items"
      :matterId="is_base_level ? null : level_id"
      :baseMatterId="is_base_level ? level_id : null"
      :showEgressProfile="true"
      :showMatrix1="true"
      :showMatrix2="true"
      :showMatrix3="true"
      :showMatrix4="true"
      :showStudyPlan="true"
      :showMatterProgram="true"
      :showCurricularMesh="true"
      :showPlannification="true"
      :showInstructional="true"
    ></NavigationBreadcrumb>

    <div class="kl-matrix-head mt-3">
      <h4 class="kl-title-md kl-matrix-title">
        {{ $t("teaching.instructionalMatrix.title") }}
        <span v-if="matter_name" class="kl-matrix-subject">
          {{ matter_name }}
        </span>
      </h4>
      <div class="kl-matrix-actions">
        <ButtonSmall
          :tooltip_text="$t('teaching.instructionalMatrix.backToDesign')"
          :click_button="goToDesign"
          :icon="'arrow-left'"
        ></ButtonSmall>
        <ButtonSmall
          :tooltip_text="$t('teaching.instructionalMatrix.print')"
          :click_button="printMatrix"
          :icon="'printer'"
        ></ButtonSmall>
      </div>
    </div>

    <b-alert v-if="error" variant="danger" show class="mt-3">
      {{ error }}
    </b-alert>
    <div v-else-if="loading" class="kl-matrix-center my-4">
      <b-spinner small></b-spinner>
    </div>

    <template v-else-if="matrix">
      <!-- La carátula del documento: qué asignatura es y qué tamaño tiene el diseño, en
           UNA tabla con sus dos pares de cabecera y fila. Sólo salen los campos que tienen
           valor: un documento con celdas en "sin registrar" no se puede entregar. -->
      <section class="kl-matrix-section">
        <h5 class="kl-title-sm kl-matrix-subtitle">
          {{ $t("teaching.instructionalMatrix.identityTitle") }}
        </h5>
        <b-table-simple
          small
          class="kl-matrix-table kl-matrix-grid kl-matrix-cover"
        >
          <b-tbody>
            <b-tr class="kl-matrix-subhead">
              <b-th v-for="row in identityRows" :key="`id-th-${row.key}`">
                {{ row.label }}
              </b-th>
            </b-tr>
            <b-tr>
              <b-td
                v-for="row in identityRows"
                :key="`id-td-${row.key}`"
                class="kl-matrix-id-value"
              >
                {{ row.value }}
              </b-td>
            </b-tr>
            <b-tr class="kl-matrix-subhead">
              <b-th
                v-for="(counter, index) in counters"
                :key="`co-th-${counter.key}`"
                :colspan="index == counters.length - 1 ? counterColspan : 1"
              >
                {{ counter.label }}
              </b-th>
            </b-tr>
            <b-tr>
              <b-td
                v-for="(counter, index) in counters"
                :key="`co-td-${counter.key}`"
                :colspan="index == counters.length - 1 ? counterColspan : 1"
                class="kl-matrix-center kl-matrix-counter-value"
              >
                {{ counter.value }}
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </section>

      <!-- La síntesis del diseño: el recorrido contado por quien lo armó (E41). Sólo
           aparece si existe; un título sobre un hueco no dice nada. -->
      <section v-if="matrix.summary" class="kl-matrix-section">
        <h5 class="kl-title-sm kl-matrix-subtitle">
          {{ $t("teaching.instructionalMatrix.summaryTitle") }}
        </h5>
        <RenderVHtml :content="matrix.summary"></RenderVHtml>
      </section>

      <!-- Los Descriptores del Diseño, zona de ARRIBA: van donde se leen en pantalla,
           antes del recorrido, porque es contexto para leerlo. Cada uno trae su propio
           título, puesto por quien lo escribió, así que la sección no lleva uno encima. -->
      <section
        v-if="topDescriptors.length > 0"
        class="kl-matrix-section"
      >
        <div
          v-for="(descriptor, index) in topDescriptors"
          :key="`descriptor-top-${index}`"
          class="kl-matrix-descriptor"
        >
          <h5 class="kl-title-sm kl-matrix-subtitle">
            {{ descriptor.title }}
          </h5>
          <RenderVHtml :content="descriptor.content"></RenderVHtml>
        </div>
      </section>

      <!-- EL RECORRIDO · los Módulos en el orden en que se dictan y adentro sus piezas.
           De cada pieza va EN TABLA sólo lo que se lee en columnas —cuándo, de qué tipo,
           qué momento cumple, cuánto dura y cuánto pesa—; lo demás baja a un bloque de
           texto debajo. Metido en celdas, un nombre de estrategia o una instrucción de
           1.500 caracteres descuadraba la tabla entera. -->
      <section class="kl-matrix-section">
        <div class="kl-matrix-section-head">
          <h5 class="kl-title-sm kl-matrix-subtitle mb-0">
            {{ $t("teaching.instructionalMatrix.pathTitle") }}
          </h5>
          <b-form-checkbox
            v-model="show_texts"
            switch
            size="sm"
            class="kl-matrix-toggle"
          >
            <span class="kl-caption">
              {{ $t("teaching.instructionalMatrix.showTexts") }}
            </span>
          </b-form-checkbox>
        </div>

        <div
          v-for="module in matrix.design_path"
          :key="`mod-${module.id || 'loose'}`"
          class="kl-matrix-module"
        >
          <!-- La barra que abre el Módulo. Es un div y no una tabla: tiene tres datos
               fijos —título, Unidades y resumen— y en tabla el navegador repartía el ancho
               según el texto de cada Módulo, así que dos barras seguidas no coincidían
               entre sí. Acá cada parte mide lo suyo y el medio se lleva lo que sobra. -->
          <div class="kl-matrix-module-head">
            <span class="kl-matrix-module-title">
              {{ moduleTitle(module) }}
            </span>
            <span class="kl-matrix-module-units">
              <span
                v-for="unit in module.units"
                :key="`mod-${module.id}-u-${unit.label}`"
                class="kl-chip kl-matrix-chip"
                :title="unit.name"
              >
                {{ unit.label }}
              </span>
            </span>
            <span class="kl-matrix-module-meta">
              {{
                $t("teaching.instructionalMatrix.moduleMeta", {
                  pieces: module.pieces.length,
                  hours: module.hours,
                  weighing: module.weighing,
                })
              }}
            </span>
          </div>

          <div
            v-for="piece in module.pieces"
            :key="`piece-${piece.id}`"
            class="kl-matrix-piece"
          >
            <!-- La cabecera va con cada pieza y no una sola vez arriba: entre una pieza y
                 la siguiente hay un bloque de texto, y sin repetirla no se sabía a qué
                 columna pertenecía cada celda. -->
            <b-table-simple small class="kl-matrix-table kl-matrix-grid">
              <b-thead>
                <b-tr class="kl-matrix-subhead">
                  <b-th>
                    {{ $t("teaching.instructionalMatrix.colWeek") }}
                  </b-th>
                  <b-th>
                    {{ $t("teaching.instructionalMatrix.colCharacter") }}
                  </b-th>
                  <b-th>{{ $t("teaching.instructionalMatrix.colPiece") }}</b-th>
                  <b-th>
                    {{ $t("teaching.instructionalMatrix.colMoments") }}
                  </b-th>
                  <b-th>
                    {{ $t("teaching.instructionalMatrix.colHours") }}
                  </b-th>
                  <b-th>
                    {{ $t("teaching.instructionalMatrix.colWeighing") }}
                  </b-th>
                </b-tr>
              </b-thead>
              <b-tbody>
                <b-tr>
                  <b-td class="kl-matrix-center kl-matrix-week">
                    {{ weekLabel(piece) }}
                  </b-td>
                  <b-td>
                    <span
                      class="kl-chip kl-matrix-chip"
                      :class="{ 'kl-chip--solid': !piece.is_activity }"
                    >
                      {{ piece.type_name }}
                    </span>
                  </b-td>
                  <b-td>
                    <div class="kl-matrix-piece-name">{{ piece.name }}</div>
                    <div v-if="piece.status" class="kl-matrix-piece-meta">
                      {{ piece.status }}
                    </div>
                  </b-td>
                  <b-td>
                    <span
                      v-for="moment in piece.moments"
                      :key="`piece-${piece.id}-m-${moment}`"
                      class="kl-chip kl-matrix-chip"
                    >
                      {{ moment }}
                    </span>
                    <span
                      v-if="piece.moments.length == 0"
                      class="kl-matrix-empty"
                    >
                      {{ $t("teaching.instructionalMatrix.noMoment") }}
                    </span>
                  </b-td>
                  <b-td class="kl-matrix-number">{{ hoursLabel(piece) }}</b-td>
                  <b-td class="kl-matrix-number">
                    {{ piece.weighing ? `${piece.weighing}%` : "—" }}
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>

            <!-- Todo lo que no se lee en columnas: los IL, con qué se trabaja, qué queda de
                 ese trabajo, los recursos, y los textos de la pieza. -->
            <div class="kl-matrix-piece-detail">
              <div
                v-for="row in pieceRows(piece)"
                :key="`piece-${piece.id}-${row.key}`"
                class="kl-matrix-detail-item"
              >
                <span class="kl-caption kl-matrix-row-label">
                  {{ row.label }}
                </span>
                <!-- Los Recursos de Apoyo y la Bibliografía se escriben con el editor en la
                     Asignatura: traen listas, negritas y enlaces. Unirlos como texto dejaba
                     las etiquetas HTML a la vista, y aplanarlos pegaba los ítems de una lista
                     unos con otros. Van por el mismo sink sanitizado que los textos. -->
                <template v-if="row.rich">
                  <span
                    v-for="(item, index) in row.rich"
                    :key="`piece-${piece.id}-${row.key}-${index}`"
                    class="kl-matrix-rich"
                  >
                    <span v-if="item.type">{{ item.type }}: </span>
                    <RenderVHtml :content="item.content"></RenderVHtml
                    ><span v-if="index < row.rich.length - 1"> · </span>
                  </span>
                </template>
                <span v-else>
                  {{ row.chips ? row.chips.join(" · ") : row.value }}
                </span>
              </div>
              <!-- Los textos convierten esto en un documento que se lee sin tener el diseño
                   abierto al lado, y por eso se pueden apagar: para revisar nada más la
                   estructura, estorban. -->
              <div
                v-for="text in show_texts ? pieceTexts(piece) : []"
                :key="`piece-${piece.id}-t-${text.key}`"
                class="kl-matrix-text"
              >
                <div class="kl-caption kl-matrix-row-label">
                  {{ text.label }}
                </div>
                <RenderVHtml :content="text.value"></RenderVHtml>
              </div>
            </div>
          </div>

          <div v-if="module.pieces.length == 0" class="kl-matrix-module-empty">
            {{ $t("teaching.instructionalMatrix.moduleEmpty") }}
          </div>
        </div>

        <div v-if="matrix.design_path.length == 0" class="kl-matrix-empty">
          {{ $t("teaching.instructionalMatrix.pathEmpty") }}
        </div>
      </section>

      <!-- LA TRIANGULACIÓN · lo que esta plataforma sabe y una planilla no: si el IL que
           se evalúa es el que se trabajó, y si lo que se enseña y lo que se mide hablan
           del mismo aprendizaje (E36). -->
      <section v-if="matrix.units.length > 0" class="kl-matrix-section">
        <h5 class="kl-title-sm kl-matrix-subtitle">
          {{ $t("teaching.instructionalMatrix.unitsTitle") }}
        </h5>
        <div
          v-for="unit in matrix.units"
          :key="`unit-${unit.unit_id}`"
          class="kl-matrix-unit"
        >
          <div class="kl-matrix-unit-head">
            <span class="kl-matrix-unit-name">
              <span class="kl-matrix-unit-label">{{ unit.label }}</span>
              {{ unit.name }}
            </span>
            <span class="kl-matrix-unit-meta">
              {{
                $t("teaching.instructionalMatrix.unitMeta", {
                  activities: unit.activity_total,
                  evaluations: unit.evaluation_total,
                })
              }}
            </span>
          </div>

          <div class="kl-matrix-piece-detail">
            <div v-if="unit.moments.length > 0" class="kl-matrix-detail-item">
              <span class="kl-caption kl-matrix-row-label">
                {{ $t("teaching.instructionalMatrix.unitMoments") }}
              </span>
              <span>
                <span
                  v-for="moment in unit.moments"
                  :key="`unit-${unit.unit_id}-m-${moment.name}`"
                  class="kl-chip kl-matrix-chip"
                  :class="{ 'kl-matrix-chip-empty': moment.count == 0 }"
                >
                  {{ moment.name }}
                  <span class="kl-matrix-counter-value">
                    {{ moment.count }}
                  </span>
                </span>
              </span>
            </div>
            <div class="kl-matrix-detail-item">
              <span class="kl-caption kl-matrix-row-label">
                {{ $t("teaching.instructionalMatrix.unitShared") }}
              </span>
              <span>{{ sharedLabel(unit) }}</span>
            </div>
          </div>

          <b-table-simple small class="kl-matrix-table kl-matrix-grid">
            <b-thead>
              <b-tr>
                <b-th>
                  {{ $t("teaching.instructionalMatrix.colIndicator") }}
                </b-th>
                <b-th>
                  {{ $t("teaching.instructionalMatrix.colWorkedBy") }}
                </b-th>
                <b-th>
                  {{ $t("teaching.instructionalMatrix.colAssessedBy") }}
                </b-th>
                <b-th>
                  {{ $t("teaching.instructionalMatrix.colDiagnosis") }}
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr
                v-for="indicator in unit.indicators"
                :key="`ind-${indicator.code}`"
              >
                <b-td>
                  <span class="kl-matrix-il-code">{{ indicator.code }}</span>
                  {{ indicator.full_sentence }}
                </b-td>
                <b-td>{{ indicator.worked_by.join(" · ") || "—" }}</b-td>
                <b-td>{{ indicator.assessed_by.join(" · ") || "—" }}</b-td>
                <b-td :class="diagnosisClass(indicator.diagnosis)">
                  {{ diagnosisLabel(indicator.diagnosis) }}
                </b-td>
              </b-tr>
              <b-tr v-if="unit.indicators.length == 0">
                <b-td colspan="4" class="kl-matrix-empty kl-matrix-center">
                  {{ $t("teaching.instructionalMatrix.unitNoIndicators") }}
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </div>
      </section>

      <!-- EL CALENDARIO · la semana no se guarda: sale de acumular las horas en el orden
           del diseño, así que esta tabla es una consecuencia, no un dato cargado. -->
      <section v-if="matrix.weekly.length > 0" class="kl-matrix-section">
        <h5 class="kl-title-sm kl-matrix-subtitle">
          {{ $t("teaching.instructionalMatrix.weeklyTitle") }}
        </h5>
        <b-table-simple small class="kl-matrix-table kl-matrix-grid">
          <b-thead>
            <b-tr>
              <b-th>
                {{ $t("teaching.instructionalMatrix.colWeek") }}
              </b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colContainer") }}</b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colMoments") }}</b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colPieces") }}</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="row in matrix.weekly" :key="`wk-${row.week}`">
              <b-td class="kl-matrix-number">{{ row.week }}</b-td>
              <b-td>
                {{ row.modules.join(" · ") || "—" }}
                <div v-if="row.units.length > 0" class="kl-matrix-piece-meta">
                  {{ row.units.join(" · ") }}
                </div>
              </b-td>
              <b-td>{{ row.moments.join(" · ") || "—" }}</b-td>
              <b-td>
                <div
                  v-for="piece in row.pieces"
                  :key="`wkp-${piece.id}`"
                  class="kl-matrix-week-piece"
                >
                  {{ piece.name }}
                  <span class="kl-matrix-piece-meta">
                    · {{ piece.type_name }}
                    <template v-if="piece.weighing">
                      · {{ piece.weighing }}%
                    </template>
                  </span>
                </div>
                <span v-if="row.pieces.length == 0" class="kl-matrix-empty">
                  —
                </span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
        <div class="kl-matrix-note">{{ hoursNote }}</div>
      </section>

      <!-- LAS EVALUACIONES · con las condiciones del instrumento, que ya están cargadas
           en la evaluación misma: duración, intentos, rúbrica y puntaje. -->
      <section class="kl-matrix-section">
        <h5 class="kl-title-sm kl-matrix-subtitle">
          {{ $t("teaching.instructionalMatrix.schemeTitle") }}
        </h5>
        <b-table-simple small class="kl-matrix-table kl-matrix-grid">
          <b-thead>
            <b-tr>
              <b-th>
                {{ $t("teaching.instructionalMatrix.colEvaluation") }}
              </b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colUnitWeek") }}</b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colCharacter") }}</b-th>
              <b-th>
                {{ $t("teaching.instructionalMatrix.colStudentEvidence") }}
              </b-th>
              <b-th>{{ indicatorsLabel }}</b-th>
              <b-th>
                {{ $t("teaching.instructionalMatrix.colInstrument") }}
              </b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colAgent") }}</b-th>
              <b-th>{{ $t("teaching.instructionalMatrix.colWeighing") }}</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="piece in matrix.evaluation_scheme"
              :key="`sc-${piece.id}`"
            >
              <b-td>{{ piece.name }}</b-td>
              <b-td>{{ unitWeekLabel(piece) }}</b-td>
              <b-td>{{ piece.type_name }}</b-td>
              <b-td>{{ piece.evidences.join(", ") || "—" }}</b-td>
              <b-td>{{ piece.indicators.join(", ") || "—" }}</b-td>
              <b-td>
                <div
                  v-for="test in piece.tests"
                  :key="`test-${test.id}`"
                  class="kl-matrix-week-piece"
                >
                  {{ test.title }}
                  <span class="kl-matrix-piece-meta">
                    {{ instrumentConditions(test) }}
                  </span>
                </div>
                <span v-if="piece.tests.length == 0" class="kl-matrix-empty">
                  {{ $t("teaching.instructionalMatrix.noInstrument") }}
                </span>
              </b-td>
              <b-td>{{ piece.work_type }} / {{ piece.examiner }}</b-td>
              <b-td class="kl-matrix-number">{{ piece.weighing }}%</b-td>
            </b-tr>
            <b-tr v-if="matrix.evaluation_scheme.length == 0">
              <b-td colspan="8" class="kl-matrix-empty kl-matrix-center">
                {{ $t("teaching.instructionalMatrix.noEvaluations") }}
              </b-td>
            </b-tr>
            <!-- El total va en la columna de Ponderación y no en una nota al pie: la suma
                 se lee contra la columna que suma. -->
            <b-tr
              v-if="matrix.evaluation_scheme.length > 0"
              class="kl-matrix-total-row"
            >
              <b-td colspan="7">
                {{ $t("teaching.instructionalMatrix.total") }}
              </b-td>
              <b-td class="kl-matrix-number">
                {{ matrix.totals.weighing }}%
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </section>

      <!-- Los Descriptores del Diseño, zona del FINAL: cierran el diseño igual que en
           pantalla. La bibliografía va después porque es de la Asignatura completa, no del
           diseño: es el anexo del documento. -->
      <section
        v-if="bottomDescriptors.length > 0"
        class="kl-matrix-section"
      >
        <div
          v-for="(descriptor, index) in bottomDescriptors"
          :key="`descriptor-bottom-${index}`"
          class="kl-matrix-descriptor"
        >
          <h5 class="kl-title-sm kl-matrix-subtitle">
            {{ descriptor.title }}
          </h5>
          <RenderVHtml :content="descriptor.content"></RenderVHtml>
        </div>
      </section>

      <section v-if="matrix.bibliography.length > 0" class="kl-matrix-section">
        <h5 class="kl-title-sm kl-matrix-subtitle">
          {{ $t("teaching.instructionalMatrix.bibliographyTitle") }}
        </h5>
        <!-- Agrupada por tipo: el tipo se repite tantas veces como fichas tenga, y
             repetirlo delante de cada una no agregaba nada. Va como rótulo del grupo y
             debajo sus fichas, que es como se lee una bibliografía. -->
        <div
          v-for="group in bibliographyGroups"
          :key="`bib-group-${group.type_name}`"
          class="kl-matrix-biblio-group"
        >
          <div v-if="group.type_name" class="kl-matrix-biblio-type">
            {{ group.type_name }}
          </div>
          <div
            v-for="resource in group.items"
            :key="`bib-${resource.id}`"
            class="kl-matrix-biblio"
          >
            <RenderVHtml :content="resource.description"></RenderVHtml>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import matterLanguageMixin from "@/mixins/matterLanguageMixin";

export default {
  name: "InstructionalMatrixView",
  // E53 · El documento es contenido de la asignatura: si está escrita en otro idioma, se
  // lee y se imprime en ese idioma, igual que el Programa y el Diseño del que sale.
  mixins: [matterLanguageMixin],
  components: {
    NavigationBreadcrumb: () =>
      import("@/components/reusable/NavigationBreadcrumb"),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
  },
  data() {
    return {
      // E52 · El documento se abre en dos niveles y el id del parámetro es de uno o de
      // otro según la ruta: `InstructionalMatrixMatterView` trae una Asignatura base y
      // `InstructionalMatrixView` una Asignatura del Perfil de Egreso. El endpoint ya
      // aceptaba los dos niveles, era la vista la que sólo sabía pedir uno.
      is_base_level: this.$route.name === "InstructionalMatrixMatterView",
      level_id: Number(this.$route.params.matter_id),
      matrix: null,
      loading: true,
      error: null,
      // Arranca encendido: el documento completo es para lo que se entra acá, y apagarlo
      // es la excepción —revisar nada más la estructura—.
      show_texts: true,
    };
  },
  computed: {
    // Los Descriptores de cada zona del diseño. Se leen con guardas porque el documento se
    // dibuja en cuanto llega el payload y un backend sin estas claves no debe romper la
    // vista completa.
    // La bibliografía por tipo, en el orden en que llega: el del catálogo de la
    // Asignatura, que es el que eligió quien la cargó. Las fichas sin tipo caen juntas en
    // un grupo sin rótulo, al final de los tipos que sí vinieron.
    bibliographyGroups() {
      const groups = [];
      const by_type = {};
      ((this.matrix && this.matrix.bibliography) || []).forEach((resource) => {
        const type_name = resource.type_name || "";
        if (!by_type[type_name]) {
          by_type[type_name] = { type_name, items: [] };
          groups.push(by_type[type_name]);
        }
        by_type[type_name].items.push(resource);
      });
      return groups;
    },
    topDescriptors() {
      return (this.matrix && this.matrix.descriptors_top) || [];
    },
    bottomDescriptors() {
      return (this.matrix && this.matrix.descriptors_bottom) || [];
    },
    // El mismo camino que arma Instructional.vue —Inicio · Programas · Matriz 2 ·
    // Programa de la Asignatura— y una capa más al final, que es esta vista. Hasta ahora
    // el breadcrumb tenía un solo item y desde el documento no se podía subir a ninguna
    // parte. El perfil llega en el payload: sin él no hay a dónde apuntar en Matriz 2, así
    // que el camino completo recién aparece cuando el documento terminó de cargar.
    breadcrumb_items() {
      // En nivel base el camino es el del Programa de la Asignatura base: no hay Perfil
      // de Egreso ni Matriz 2 de dónde venir.
      if (this.is_base_level) {
        return [
          {
            text: this.$t("reusable.breadcrumb.home"),
            to: "/home",
          },
          {
            text: this.$getVisibleNames(
              "mesh.egressprofilematter",
              true,
              "Asignaturas"
            ),
            to: "/matter-list",
          },
          {
            text: this.$getVisibleNames(
              "manual.matter_program",
              false,
              "Programa de la Asignatura"
            ),
            to: `/old-matter/form/${this.level_id}`,
            dropdownOptions: ["showInstructional"],
          },
          {
            text: this.$t("teaching.instructionalMatrix.breadcrumb"),
            active: true,
          },
        ];
      }
      const profileId = this.matrix ? this.matrix.general.egress_profile_id : null;
      const breadcrumb = [];
      if (profileId) {
        breadcrumb.push(
          {
            text: this.$t("reusable.breadcrumb.home"),
            to: "/home",
          },
          {
            text: this.$getVisibleNames("mesh.career", true, "Programas"),
            to: "/careers",
          },
          {
            text: this.$getVisibleNames("manual.matriz_2", false, "Matriz 2"),
            to: `/matrix2/form/${profileId}`,
            dropdownOptions: [
              "showEgressProfile",
              "showMatrix1",
              "showMatrix2",
              "showMatrix3",
              "showMatrix4",
              "showStudyPlan",
              "showCurricularMesh",
            ],
          },
          {
            text: this.$getVisibleNames(
              "manual.matter_program",
              false,
              "Programa de la Asignatura"
            ),
            to: `/matter/form/${this.level_id}`,
            dropdownOptions: [
              "showMatterProgram",
              "showPlannification",
              "showInstructional",
            ],
          }
        );
      }
      breadcrumb.push({
        text: this.$t("teaching.instructionalMatrix.breadcrumb"),
        active: true,
      });
      return breadcrumb;
    },
    matter_name() {
      return this.matrix ? this.matrix.general.matter_name : "";
    },
    indicatorsLabel() {
      return this.$getVisibleNames(
        "teaching.evaluationcriteriamicro",
        true,
        "Indicadores de Logro"
      );
    },
    // La identificación: sólo los campos que tienen valor. Es la diferencia entre un
    // documento y una plantilla — la plantilla muestra el hueco, el documento no se
    // imprime con filas vacías.
    identityRows() {
      const general = this.matrix.general;
      const label = (key) => this.$t(`teaching.instructionalMatrix.${key}`);
      return [
        { key: "code", label: label("fieldCode"), value: general.matter_code },
        {
          key: "profile",
          label: label("fieldProfile"),
          value: general.egress_profile,
        },
        // E52 · En el documento de la Asignatura base no hay Programa, ni nivel del plan,
        // ni modalidad del Perfil: tres de las siete columnas de la carátula quedaban
        // fuera y lo que sobrevivía era un código y dos números. Estas tres filas son lo
        // que la Asignatura base SÍ declara, y por eso ocupan el lugar de las otras.
        // Sólo salen cuando no hay Perfil: en el documento del plan la carátula ya está
        // completa y agregarle columnas sería cambiar un documento que ya se revisó.
        {
          key: "matter",
          label: this.$getVisibleNames(
            "teaching.matter",
            false,
            "Asignatura Base"
          ),
          value: general.egress_profile ? "" : general.matter_name,
        },
        {
          key: "formation_area",
          label: this.$getVisibleNames(
            "mesh.formationarea",
            false,
            "Área de Formación"
          ),
          value: general.egress_profile ? "" : general.formation_area,
        },
        {
          key: "matter_types",
          label: this.$getVisibleNames(
            "mesh.mattertype",
            true,
            "Tipos de Asignatura"
          ),
          value: general.egress_profile ? "" : general.matter_types,
        },
        {
          key: "level",
          label: label("fieldPlanLevel"),
          value: general.plan_level,
        },
        {
          key: "credits",
          label: label("fieldCredits"),
          value: general.credits,
        },
        { key: "weeks", label: label("fieldWeeks"), value: general.weeks },
        {
          key: "modality",
          label: label("fieldModality"),
          value: general.modality,
        },
        { key: "method", label: label("fieldMethod"), value: general.method },
      ].filter((row) => row.value || row.value === 0);
    },
    counters() {
      const totals = this.matrix.totals;
      const hours = this.matrix.hours;
      const label = (key) => this.$t(`teaching.instructionalMatrix.${key}`);
      return [
        {
          key: "modules",
          label: label("counterModules"),
          value: totals.modules,
        },
        {
          key: "activities",
          label: label("counterActivities"),
          value: totals.activities,
        },
        {
          key: "evaluations",
          label: label("counterEvaluations"),
          value: totals.evaluations,
        },
        {
          key: "hours",
          label: label("counterHours"),
          value: `${hours.assigned_hours} / ${hours.total_hours}`,
        },
        {
          key: "autonomous",
          label: label("counterAutonomous"),
          value: hours.autonomous_hours,
        },
        {
          key: "weighing",
          label: label("counterWeighing"),
          value: `${totals.weighing}%`,
        },
      ];
    },
    // Las dos cabeceras de la carátula no tienen la misma cantidad de columnas, así que la
    // última celda de la segunda estira para cuadrar con la primera. Se calcula porque la
    // identificación esconde los campos sin valor y el número cambia de asignatura en
    // asignatura.
    counterColspan() {
      return Math.max(1, this.identityRows.length - this.counters.length + 1);
    },
    hoursNote() {
      const hours = this.matrix.hours;
      return this.$t("teaching.instructionalMatrix.hoursNote", {
        total: hours.total_hours,
        weeks: hours.weeks,
        weekly: hours.weekly_hours,
      });
    },
  },
  methods: {
    fetchMatrix() {
      const level = this.is_base_level ? "matter" : "egress_profile_matter";
      this.$restful
        .Get(`/teaching/instructional_matrix/?${level}=${this.level_id}`)
        .then((response) => {
          this.matrix = response;
          this.loading = false;
          // El idioma llega en el payload: la vista no tiene de dónde sacarlo sola.
          this.applyMatterLanguageById(response.general.default_language);
        })
        .catch(() => {
          this.error = this.$t("teaching.instructionalMatrix.loadError");
          this.loading = false;
        });
    },
    goToDesign() {
      this.$router.push({
        name: this.is_base_level
          ? "InstructionalMatterView"
          : "InstructionalView",
        params: { matter_id: this.level_id },
      });
    },
    printMatrix() {
      window.print();
    },
    moduleTitle(module) {
      if (module.loose)
        return this.$t("teaching.instructionalMatrix.loosePieces");
      if (module.title) return module.title;
      return this.$t("teaching.instructionalMatrix.moduleUntitled", {
        order: module.order,
      });
    },
    // La semana, o el motivo por el que la pieza no la tiene: el campo vacío no distinguía
    // "no corresponde" de "falta cargar algo".
    weekLabel(piece) {
      if (piece.week == null)
        return this.$t("teaching.instructionalMatrix.weekNone");
      if (piece.week_to && piece.week_to != piece.week)
        return this.$t("teaching.instructionalMatrix.weekRange", {
          from: piece.week,
          to: piece.week_to,
        });
      return this.$t("teaching.instructionalMatrix.week", {
        week: piece.week,
      });
    },
    // Las horas de la pieza en una sola celda: las de aula y, si tiene, las autónomas.
    // Van juntas porque son el mismo dato leído en dos lugares —lo que ocupa en el
    // calendario y lo que se le pide al estudiante fuera de él—.
    hoursLabel(piece) {
      const parts = [];
      if (piece.hours)
        parts.push(
          this.$t("teaching.instructionalMatrix.hoursShort", {
            hours: piece.hours,
          })
        );
      if (piece.autonomous_work_hours)
        parts.push(
          this.$t("teaching.instructionalMatrix.autonomousShort", {
            hours: piece.autonomous_work_hours,
          })
        );
      return parts.length > 0 ? parts.join(" + ") : "\u2014";
    },
    // Las filas de datos de una pieza, en el orden del triángulo: qué aprendizaje toca,
    // con qué se trabaja, qué queda de ese trabajo y con qué se mide. La fila sin dato no
    // se dibuja.
    pieceRows(piece) {
      const label = (key) => this.$t(`teaching.instructionalMatrix.${key}`);
      const participation = piece.suggested_students
        ? `${piece.work_type} · ${this.$t(
            "teaching.instructionalMatrix.studentsShort",
            { students: piece.suggested_students }
          )}`
        : piece.work_type;
      return [
        { key: "il", label: this.indicatorsLabel, chips: piece.indicators },
        {
          key: "strategies",
          label: label("rowStrategies"),
          chips: piece.strategies,
        },
        {
          key: "evidences",
          label: label("rowEvidences"),
          chips: piece.evidences,
        },
        {
          key: "environments",
          label: label("rowEnvironments"),
          chips: piece.study_environments,
        },
        {
          key: "resources",
          label: label("rowResources"),
          rich: piece.support_resources.map((resource) => ({
            type: resource.type,
            content: resource.content,
          })),
        },
        // La bibliografía de la pieza. Existía en el payload y no se dibujaba en ninguna
        // parte: la del final del documento es la de la Asignatura completa, y leyendo una
        // Actividad no había forma de saber cuál de todos esos títulos es el suyo.
        {
          key: "bibliography",
          label: label("rowBibliography"),
          rich: (piece.bibliography || []).map((description) => ({
            type: "",
            content: description,
          })),
        },
        {
          key: "contents",
          label: label("rowContents"),
          chips: piece.contents.map((content) =>
            this.$htmlToPlainText(content)
          ),
        },
        {
          key: "participation",
          label: label("rowParticipation"),
          value: participation,
        },
        {
          key: "instrument",
          label: label("colInstrument"),
          value: piece.tests
            .map((test) =>
              `${test.title} ${this.instrumentConditions(test)}`.trim()
            )
            .join(" · "),
        },
      ].filter((row) => {
        if (row.rich) return row.rich.length > 0;
        if (row.chips) return row.chips.length > 0;
        return !!row.value;
      });
    },
    pieceTexts(piece) {
      const label = (key) => this.$t(`teaching.instructionalMatrix.${key}`);
      return [
        {
          key: "description",
          label: label("textDescription"),
          value: piece.description,
        },
        {
          key: "orientation",
          label: label("textOrientation"),
          value: piece.orientation,
        },
        {
          key: "student",
          label: label("textStudent"),
          value: piece.description_for_student,
        },
        {
          key: "professor",
          label: label("textProfessor"),
          value: piece.professor_instructions,
        },
        // Los Descriptores de la pieza van con los textos y no en una fila de datos: cada
        // uno es un texto con su propio título, puesto por el Diseñador Instruccional justo
        // para contar algo que no tiene campo propio. Por eso también obedecen al
        // interruptor de textos: sin él la Actividad se lee incompleta.
        ...(piece.descriptors || []).map((descriptor, index) => ({
          key: `descriptor-${index}`,
          label: descriptor.title,
          value: descriptor.content,
        })),
      ].filter((text) => !!text.value);
    },
    // Las estrategias y evidencias que aparecen a los dos lados. Cuando un lado no declaró
    // nada NO se dice "no coinciden": el hallazgo es que falta declararlas, que es otro
    // problema (E48).
    sharedLabel(unit) {
      const parts = [];
      if (unit.shared_strategies.length > 0)
        parts.push(
          this.$t("teaching.instructionalMatrix.sharedStrategies", {
            names: unit.shared_strategies.join(", "),
          })
        );
      else if (unit.strategies_comparable)
        parts.push(this.$t("teaching.instructionalMatrix.noSharedStrategies"));
      if (unit.shared_evidences.length > 0)
        parts.push(
          this.$t("teaching.instructionalMatrix.sharedEvidences", {
            names: unit.shared_evidences.join(", "),
          })
        );
      else if (unit.evidences_comparable)
        parts.push(this.$t("teaching.instructionalMatrix.noSharedEvidences"));
      if (parts.length == 0)
        return this.$t("teaching.instructionalMatrix.notComparable");
      return parts.join(" · ");
    },
    diagnosisLabel(diagnosis) {
      return this.$t(`teaching.instructionalMatrix.diagnosis_${diagnosis}`);
    },
    diagnosisClass(diagnosis) {
      return {
        triangulado: "kl-matrix-ok",
        se_evalua_sin_trabajarse: "kl-matrix-danger",
        se_trabaja_sin_evaluarse: "kl-matrix-warning",
        sin_trabajar_ni_evaluar: "kl-matrix-danger",
      }[diagnosis];
    },
    unitWeekLabel(piece) {
      const units = piece.units.join(", ") || "—";
      if (!piece.week) return units;
      return `${units} / S${piece.week}`;
    },
    instrumentConditions(test) {
      const parts = [];
      if (test.duration) parts.push(test.duration);
      if (test.attempts)
        parts.push(
          this.$t("teaching.instructionalMatrix.attempts", {
            attempts: test.attempts,
          })
        );
      if (test.has_rubric)
        parts.push(this.$t("teaching.instructionalMatrix.withRubric"));
      if (test.max_score)
        parts.push(
          this.$t("teaching.instructionalMatrix.maxScore", {
            score: test.max_score,
          })
        );
      return parts.length > 0 ? `· ${parts.join(" · ")}` : "";
    },
  },
  created() {
    this.fetchMatrix();
  },
};
</script>
<style scoped>
/* El contenido enriquecido de una fila tiene que leerse EN la fila, al lado de su rótulo:
   RenderVHtml monta un div con su propio padding y párrafos de bloque, así que se aplana
   desde acá. El componente es compartido y no se le toca el estilo propio. */
.kl-matrix-rich,
.kl-matrix-rich ::v-deep .rich-text-content,
.kl-matrix-rich ::v-deep p {
  display: inline;
}
.kl-matrix-rich ::v-deep .rich-text-content,
.kl-matrix-rich ::v-deep p {
  padding: 0;
  margin: 0;
}
/* Una lista sí se mantiene como lista: es la forma en la que se escribió el recurso. */
.kl-matrix-rich ::v-deep ol,
.kl-matrix-rich ::v-deep ul {
  display: inline-block;
  vertical-align: top;
  margin: 0;
  padding-left: 1.2rem;
  text-align: left;
}

/* E51 · Todo sale de los tokens de `styles_kl.css`: ni un color ni un tamaño a mano. Los
   títulos y el cuerpo usan las utilitarias globales (kl-title-md / kl-title-sm / kl-text /
   kl-caption) y las etiquetas la clase única `kl-chip`. */
.kl-matrix {
  text-align: left;
  color: var(--kl-text-body);
}
.kl-matrix-head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
}
.kl-matrix-title {
  margin: 0;
  text-align: center;
  padding: 0 var(--kl-space-6);
}
.kl-matrix-subject {
  display: block;
  font-size: var(--kl-fs-title-sm);
  font-weight: var(--kl-fw-semibold);
  color: var(--kl-text-muted);
}
.kl-matrix-actions {
  position: absolute;
  right: 0;
  top: 0;
}
.kl-matrix-section {
  margin-top: var(--kl-space-5);
}
/* Los títulos de sección ya no llevan línea: con ocho secciones, cada tabla con su marco y
   cada Módulo con su barra de color, las líneas horizontales sólo sumaban rayas. */
.kl-matrix-section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--kl-space-3);
  margin-bottom: var(--kl-space-2);
}
.kl-matrix-subtitle {
  margin-bottom: var(--kl-space-2);
}

/* ---------- Carátula ---------- */
.kl-matrix-id-value {
  font-weight: var(--kl-fw-semibold);
}
.kl-matrix-counter-value {
  font-weight: var(--kl-fw-bold);
  font-variant-numeric: tabular-nums;
}

/* ---------- El recorrido ---------- */
/* El Módulo ya no es un marco: es su barra y, debajo, sus piezas. Lo que antes separaba un
   Módulo del siguiente era ese marco, así que ahora tiene que separarlos el espacio: sin
   él, la barra del Módulo 2 quedaba pegada al último texto del Módulo 1 y todo se leía
   como un bloque corrido. */
.kl-matrix-module {
  margin-bottom: var(--kl-space-6);
}
/* Tres anchos fijos: el título y el resumen miden lo suyo y no se encogen (`flex: 0 0
   auto`), y el medio se queda con lo que sobra. Es lo que una tabla no daba: ahí el ancho
   lo decidía el texto de cada Módulo y dos barras seguidas quedaban descalzadas. */
.kl-matrix-module-head {
  display: flex;
  align-items: center;
  gap: var(--kl-space-2);
  background: var(--kl-color-accent-medium);
  color: var(--kl-text-on-primary);
  padding: var(--kl-space-2) var(--kl-space-3);
}
/* El chip trae margen superior para separarse del chip de la fila de arriba cuando van en
   bloque; en una barra de una sola línea ese margen lo bajaba respecto del título. */
.kl-matrix-module-head .kl-matrix-chip {
  margin: 0 var(--kl-space-1) 0 0;
}
.kl-matrix-module-title {
  flex: 0 0 auto;
  font-weight: var(--kl-fw-bold);
  white-space: nowrap;
}
/* El medio absorbe el espacio libre y es el único que puede encogerse: si un Módulo tiene
   seis Unidades, se aprietan los chips y no el título ni el resumen. */
.kl-matrix-module-units {
  flex: 1 1 auto;
  min-width: 0;
}
/* El resumen del Módulo —piezas, horas, ponderación— es un dato del documento, no una
   nota al pie: con el tamaño de caption sobre fondo de color se perdía. */
.kl-matrix-module-meta {
  flex: 0 0 auto;
  font-size: var(--kl-fs-small);
  font-weight: var(--kl-fw-semibold);
  white-space: nowrap;
  text-align: right;
}
.kl-matrix-module-empty {
  color: var(--kl-text-muted);
  font-style: italic;
  font-size: var(--kl-fs-caption);
  padding: var(--kl-space-2) var(--kl-space-3);
}
/* Sin marco ni línea alrededor: lo que separa una pieza de la siguiente es el espacio. */
.kl-matrix-piece {
  margin-top: var(--kl-space-4);
}
.kl-matrix-piece + .kl-matrix-piece {
  margin-top: var(--kl-space-5);
}
.kl-matrix-piece-name {
  font-weight: var(--kl-fw-semibold);
}
.kl-matrix-week {
  font-weight: var(--kl-fw-bold);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
/* El contenido que no va en columnas. Cada dato es una línea con su etiqueta a la
   izquierda, para que el ojo baje por las etiquetas y no tenga que leerlo todo. */
.kl-matrix-piece-detail {
  margin-top: var(--kl-space-2);
}
.kl-matrix-detail-item {
  display: flex;
  align-items: baseline;
  gap: var(--kl-space-2);
  margin-bottom: var(--kl-space-1);
}
.kl-matrix-row-label {
  flex: 0 0 13rem;
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
  font-weight: var(--kl-fw-semibold);
}
.kl-matrix-text {
  margin-top: var(--kl-space-3);
}

/* ---------- Triangulación ---------- */
.kl-matrix-unit {
  margin-bottom: var(--kl-space-4);
}
.kl-matrix-unit-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--kl-space-2);
  background: var(--kl-surface-muted);
  border: 1px solid var(--kl-border-soft);
  padding: var(--kl-space-2) var(--kl-space-3);
}
.kl-matrix-unit-label {
  font-weight: var(--kl-fw-bold);
  margin-right: var(--kl-space-1);
}
.kl-matrix-unit-name {
  font-weight: var(--kl-fw-semibold);
}
/* Cuántas piezas trabajan la Unidad: va a la derecha y destacado, porque es lo que dice de
   un vistazo si la Unidad está cubierta. */
.kl-matrix-unit-meta {
  margin-left: auto;
  font-size: var(--kl-fs-small);
  font-weight: var(--kl-fw-semibold);
  white-space: nowrap;
}
/* El momento que la Unidad no trabaja, y el IL que no cierra el triángulo: se marcan con
   la rampa de estado del sistema, que es donde el rojo ya significa "acá falta algo". */
.kl-matrix-chip-empty {
  color: var(--kl-color-danger);
  border-color: var(--kl-color-danger);
  background: var(--kl-color-danger-soft);
}
.kl-matrix-ok {
  color: var(--kl-color-success);
  font-weight: var(--kl-fw-semibold);
}
.kl-matrix-warning {
  color: var(--kl-color-warning);
  font-weight: var(--kl-fw-semibold);
}
.kl-matrix-danger {
  color: var(--kl-color-danger);
  font-weight: var(--kl-fw-semibold);
}

/* ---------- Tablas ----------
   `b-table-simple` trae el `.table` de Bootstrap; acá va lo propio del documento. Esquina
   cuadrada a propósito: con ocho tablas una tras otra, redondeadas cada una se leía como
   una tarjeta aparte en vez de como una sección del mismo papel. */
.kl-matrix-table {
  font-size: var(--kl-table-fs);
  color: var(--kl-table-text);
  border: 1px solid var(--kl-table-border);
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 0;
}
.kl-matrix-table ::v-deep th,
.kl-matrix-table ::v-deep td {
  vertical-align: top;
  border-top: 1px solid var(--kl-table-border);
  /* La línea vertical es lo que permite seguir una columna cuando las celdas tienen varias
     líneas: sin ella se leían como un bloque corrido. */
  border-left: 1px solid var(--kl-table-border);
  padding: var(--kl-space-2);
  /* El contenido a la izquierda SIEMPRE: la vista centra el texto más arriba y eso dejaba
     cada celda con su propio eje. */
  text-align: left;
}
.kl-matrix-table ::v-deep th:first-child,
.kl-matrix-table ::v-deep td:first-child {
  border-left: 0;
}
.kl-matrix-table ::v-deep tr:first-child th,
.kl-matrix-table ::v-deep tr:first-child td {
  border-top: 0;
}
/* La cabecera va centrada: es una etiqueta corta sobre su columna. */
.kl-matrix-grid ::v-deep thead th,
.kl-matrix-grid ::v-deep .kl-matrix-subhead > th {
  background-color: var(--kl-color-accent-medium);
  color: var(--kl-text-on-primary);
  font-size: var(--kl-table-header-fs);
  font-weight: var(--kl-fw-bold);
  text-transform: uppercase;
  letter-spacing: var(--kl-ls-wide);
  text-align: center;
  vertical-align: middle;
  border-left-color: var(--kl-surface);
}
.kl-matrix-grid ::v-deep tbody tr:nth-child(even) {
  background-color: var(--kl-table-row-alt-bg);
}
/* La carátula es un encabezado, no una lista de datos: sus filas van en blanco. */
.kl-matrix-cover ::v-deep tbody tr {
  background-color: var(--kl-surface);
}
.kl-matrix-week-piece {
  margin-bottom: var(--kl-space-1);
}
.kl-matrix-il-code {
  font-weight: var(--kl-fw-semibold);
  margin-right: var(--kl-space-1);
}
/* El chip ya trae su forma de `.kl-chip`; acá sólo la separación entre varios seguidos. */
.kl-matrix-chip {
  display: inline-flex;
  margin: var(--kl-space-1) var(--kl-space-1) 0 0;
}
.kl-matrix-piece-meta,
.kl-matrix-note {
  color: var(--kl-text-muted);
  font-size: var(--kl-fs-caption);
}
.kl-matrix-note {
  margin-top: var(--kl-space-1);
}
.kl-matrix-empty {
  color: var(--kl-text-muted);
  font-style: italic;
  font-size: var(--kl-fs-caption);
}
.kl-matrix-total-row {
  font-weight: var(--kl-fw-semibold);
  background-color: var(--kl-surface-muted);
}
.kl-matrix-biblio {
  font-size: var(--kl-fs-small);
  margin-bottom: var(--kl-space-1);
}
/* El rótulo del tipo manda sobre su grupo, como el subtítulo de una sección chica. */
.kl-matrix-biblio-type {
  font-weight: 600;
  margin-bottom: var(--kl-space-1);
}
.kl-matrix-biblio-group + .kl-matrix-biblio-group {
  margin-top: var(--kl-space-2);
}
/* La ficha bibliográfica se escribe con el editor, así que su párrafo es un bloque: el
   tipo —"Básica", "Complementaria"— quedaba en una línea y la ficha en la siguiente, y
   cada entrada ocupaba dos. Se aplana el CONTENIDO, no la fila: cada entrada sigue siendo
   una línea. El componente es compartido, así que se ajusta desde afuera. */
.kl-matrix-biblio ::v-deep .rich-text-content,
.kl-matrix-biblio ::v-deep p {
  display: inline;
  padding: 0;
  margin: 0;
}
/* Un bloque de texto del diseño se separa del siguiente: son secciones distintas, cada
   una con su título. */
.kl-matrix-descriptor + .kl-matrix-descriptor {
  margin-top: var(--kl-space-3);
}
.kl-matrix-number {
  text-align: right;
}
/* Clase propia y no la utilitaria de Bootstrap: el `text-align: left` de la celda le gana
   por especificidad. */
/* El breadcrumb vive dentro del contenedor que lleva .kl-text, y .kl-text baja la letra
   a --kl-fs-body (14px). Eso lo heredan sus items simples, que son spans, pero no los que
   abren menú: esos son un .btn de Bootstrap, que declara font-size: 1rem. El resultado
   era un breadcrumb con dos tamaños mezclados según el item tuviera menú o no. Acá se le
   devuelve el tamaño que tiene en el resto de la plataforma: la tipografía del documento
   es para el documento, el breadcrumb es navegación y no parte de la hoja. */
.kl-matrix ::v-deep .breadcrumb {
  font-size: 1rem;
}
.kl-matrix-center {
  text-align: center;
}

/* La hoja va horizontal: la tabla del calendario y la de evaluaciones no entran en
   vertical —el navegador recorta lo que sobra, no lo pasa a la página siguiente—. */
@page {
  size: landscape;
  margin: 10mm;
}
@media print {
  /* El botón de imprimir y el interruptor de los textos son controles de pantalla: en el
     papel no se pueden tocar y ocupan lugar. */
  .kl-matrix-actions,
  .kl-matrix-toggle {
    display: none !important;
  }
  /* Una tabla larga TIENE que poder partirse entre páginas: con `avoid` en la sección, el
     navegador la empujaba entera a la hoja siguiente y recortaba lo que no cabía. Lo que
     no se parte es la fila, que es la unidad que se lee. */
  .kl-matrix-section,
  .kl-matrix-module,
  .kl-matrix-unit {
    page-break-inside: auto;
  }
  /* La pieza SÍ se puede partir entre páginas: con los textos encendidos mide más que una
     hoja —las instrucciones del estudiante solas llegan a 1.800 caracteres—, y pedir que
     no se parta obligaba al navegador a empujarla entera a la hoja siguiente, dejando en
     blanco lo que quedaba de la anterior. Lo que no se parte es la fila de la tabla. */
  .kl-matrix-piece {
    page-break-inside: auto;
  }
  .kl-matrix-table ::v-deep tr {
    page-break-inside: avoid;
  }
  /* Una barra de Módulo o una cabecera de pieza al pie de la hoja, con su contenido en la
     siguiente, es un título huérfano: se baja con lo que encabeza. */
  .kl-matrix-module-head,
  .kl-matrix-unit-head,
  .kl-matrix-piece .kl-matrix-table {
    page-break-after: avoid;
  }
  /* En papel baja el tamaño de letra; el ancho lo sigue repartiendo el navegador. Con
     `table-layout: fixed` las columnas se repartían por igual y aplastaba la barra del
     Módulo, que es justo la que necesita medir su contenido. Lo que evita que una palabra
     larga descuadre la tabla es el quiebre de abajo, no un ancho impuesto. */
  .kl-matrix-table {
    font-size: var(--kl-fs-caption);
  }
  .kl-matrix-table ::v-deep th,
  .kl-matrix-table ::v-deep td {
    padding: var(--kl-space-1);
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  /* Los fondos de cabecera NO son decoración: sin ellos todas las filas se ven iguales y,
     peor, la cabecera lleva el texto en blanco, así que queda blanca sobre blanca y
     desaparece. El navegador los quita al imprimir, de modo que hay que forzarlos —con
     `!important`, porque la hoja de impresión del navegador pisa el color de fondo—. */
  .kl-matrix,
  .kl-matrix ::v-deep * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .kl-matrix-grid ::v-deep thead th,
  .kl-matrix-grid ::v-deep .kl-matrix-subhead > th {
    background-color: var(--kl-color-accent-medium) !important;
    color: var(--kl-text-on-primary) !important;
  }
  .kl-matrix-module-head {
    background: var(--kl-color-accent-medium) !important;
    color: var(--kl-text-on-primary) !important;
  }
  .kl-matrix-unit-head,
  .kl-matrix-total-row {
    background-color: var(--kl-surface-muted) !important;
  }
  .kl-matrix-chip-empty {
    background: var(--kl-color-danger-soft) !important;
    color: var(--kl-color-danger) !important;
  }
}
</style>
